import React, { useState } from 'react';
import { 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Link, 
  Alert,
  CircularProgress
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { passwordResetService } from '../services/passwordResetService';
import '../styles/pages/_esqueci-senha.scss';

const EsqueciSenha: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<{ email?: string }>({});

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setErrors({});

    // Validate email
    if (!email.trim()) {
      setErrors({ email: 'Email é obrigatório' });
      return;
    }

    if (!validateEmail(email)) {
      setErrors({ email: 'Email inválido' });
      return;
    }

    setIsLoading(true);

    try {
      await passwordResetService.requestReset({ email });
      setIsSubmitted(true);
      toast.success('Email de recuperação enviado com sucesso!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Erro ao enviar email de recuperação. Tente novamente.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors({ ...errors, email: undefined });
    }
  };

  if (isSubmitted) {
    return (
      <Box className="esqueci-senha-page" sx={{ textAlign: 'center', maxWidth: 400, mx: 'auto' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
          Email Enviado
        </Typography>
        
        <Alert severity="success" sx={{ mb: 3 }}>
          Enviamos um email com instruções para redefinir sua senha.
          Verifique sua caixa de entrada e spam.
        </Alert>

        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Não recebeu o email? Verifique se o endereço está correto ou tente novamente.
        </Typography>

        <Button
          variant="outlined"
          onClick={() => {
            setIsSubmitted(false);
            setEmail('');
          }}
          sx={{ mr: 2 }}
        >
          Tentar Novamente
        </Button>

        <Button
          component={RouterLink}
          to="/inicio/login"
          variant="text"
        >
          Voltar ao Login
        </Button>
      </Box>
    );
  }

  return (
    <Box className="esqueci-senha-page" sx={{ maxWidth: 400, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 1, textAlign: 'center' }}>
        Esqueci minha senha
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
        Digite seu email para receber instruções de recuperação de senha
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={handleEmailChange}
          error={!!errors.email}
          helperText={errors.email}
          disabled={isLoading}
          sx={{ mb: 3 }}
          autoComplete="email"
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          sx={{ mb: 2, py: 1.5 }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Enviar Email de Recuperação'
          )}
        </Button>
      </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Link
          component={RouterLink}
          to="/inicio/login"
          variant="body2"
          sx={{ textDecoration: 'none' }}
        >
          Voltar ao login
        </Link>
      </Box>
    </Box>
  );
};

export default EsqueciSenha; 